import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import JobCostingStatsBar from '@/components/costing/JobCostingStatsBar';
import JobCostingTable from '@/components/costing/JobCostingTable';
import JobCostingDetailSheet from '@/components/costing/JobCostingDetailSheet';
import LogMaterialDialog from '@/components/costing/LogMaterialDialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, ArrowLeft, RefreshCw, Download, Plus } from 'lucide-react';
import { useIsWideScreen } from '@/hooks/use-media-query';
import {
  fetchCostingData,
  buildCostingRows,
  computeOverviewStats,
  applyCostingFilter,
  rowsToCsv,
  downloadCsv,
} from '@/lib/jobCostingData';
import { toast } from 'sonner';

export default function JobCostingPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isWide = useIsWideScreen();
  const [jobs, setJobs] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('margin_asc');
  const [selectedJobId, setSelectedJobId] = useState('all');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [logOpen, setLogOpen] = useState(false);
  const [logJob, setLogJob] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await fetchCostingData();
      setJobs(data.jobs);
      setTimeEntries(data.timeEntries);
      setEstimates(data.estimates);
    } catch (e) {
      setError(e?.message || t('jobCosting.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-link: ?jobId=<uuid> auto-opens the detail sheet for that job —
  // ONCE, on first load. Opening a job from the list now also writes
  // ?jobId=, and this must not re-fire on later data changes (a logged
  // material) and narrow the list's scope to that one job.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (loading || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const id = searchParams.get('jobId');
    if (!id) return;
    const rows = buildCostingRows({ jobs, timeEntries, estimates });
    const found = rows.find((r) => r.job_id === id);
    if (found) {
      setSelectedJobId(id);
      setDetailRow(found);
      setDetailOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jobs, timeEntries, estimates]);

  const allRows = useMemo(
    () => buildCostingRows({ jobs, timeEntries, estimates }),
    [jobs, timeEntries, estimates]
  );

  const scopedRows = useMemo(() => {
    if (selectedJobId === 'all') return allRows;
    return allRows.filter((r) => r.job_id === selectedJobId);
  }, [allRows, selectedJobId]);

  const filteredRows = useMemo(
    () => applyCostingFilter(scopedRows, { search, statusFilter, sort }),
    [scopedRows, search, statusFilter, sort]
  );

  const stats = useMemo(() => computeOverviewStats(scopedRows), [scopedRows]);

  // The open job rides the URL (?jobId=) so the browser's Back button
  // returns to the list instead of leaving Job Tracking — same as Active Jobs.
  const handleOpen = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    if (row?.job_id) setSearchParams({ jobId: row.job_id });
  };

  const closeDetail = () => {
    setDetailOpen(false);
    if (searchParams.get('jobId')) setSearchParams({});
  };

  // Back button (param gone) closes the detail.
  useEffect(() => {
    if (!searchParams.get('jobId') && detailOpen) setDetailOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Wide screens get the job as a full page in place of the list.
  const showFullPage = isWide && detailOpen && !!detailRow;
  useEffect(() => {
    if (showFullPage) window.scrollTo({ top: 0 });
  }, [showFullPage, detailRow?.job_id]);

  const handleLogMaterial = (job) => {
    setLogJob(job);
    setLogOpen(true);
  };

  const handleJobUpdated = (updatedJob) => {
    if (!updatedJob?.id) return;
    setJobs((cur) => {
      const exists = cur.some((j) => j.id === updatedJob.id);
      return exists
        ? cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
        : [updatedJob, ...cur]; // e.g. a freshly-created Miscellaneous bucket
    });
  };

  // When a job gets an update (material log add/remove), refresh the detail row.
  useEffect(() => {
    if (!detailRow?.job_id) return;
    const updated = allRows.find((r) => r.job_id === detailRow.job_id);
    if (updated) setDetailRow(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows]);

  const handleExport = () => {
    const csv = rowsToCsv(filteredRows);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`kocore-job-costing-${stamp}.csv`, csv);
    toast.success(t('jobCosting.exportedToast', { count: filteredRows.length }));
  };

  if (showFullPage) {
    return (
      <div data-testid="job-costing-page">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeDetail}
            className="text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-xs"
            data-testid="costing-full-page-back"
          >
            <ArrowLeft size={14} className="mr-2" />
            {t('jobCosting.backToList', { defaultValue: 'Back to job tracking' })}
          </Button>
        </div>
        <JobCostingDetailSheet
          fullPage
          open
          onOpenChange={(o) => {
            if (!o) closeDetail();
          }}
          row={detailRow}
          onUpdated={handleJobUpdated}
          onLogMaterial={handleLogMaterial}
        />
        <LogMaterialDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          job={logJob}
          jobs={jobs}
          onLogged={handleJobUpdated}
        />
      </div>
    );
  }

  return (
    <div data-testid="job-costing-page">
      <PageHeader
        eyebrow={t('jobCosting.eyebrow')}
        title={t('jobCosting.title')}
        subtitle={t('jobCosting.subtitle')}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="costing-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t('jobCosting.refresh')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={filteredRows.length === 0}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="costing-export"
            >
              <Download size={14} className="mr-2" />
              {t('jobCosting.exportCsv')}
            </Button>
            <Button
              size="sm"
              onClick={() => handleLogMaterial(null)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="costing-log-material"
            >
              <Plus size={14} className="mr-2" />
              {t('jobCosting.logMaterial')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="costing-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {/* Job scope selector */}
      <div
        className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3"
        data-testid="costing-scope"
      >
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          {t('jobCosting.scope')}
        </div>
        <Select value={selectedJobId} onValueChange={setSelectedJobId}>
          <SelectTrigger
            className="h-10 w-full sm:w-[340px] bg-[hsl(var(--muted))] border-border"
            data-testid="costing-job-picker"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
            <SelectItem value="all">{t('jobCosting.allJobs', { count: allRows.length })}</SelectItem>
            {allRows.map((r) => (
              <SelectItem key={r.job_id} value={r.job_id}>
                {r.name} {'\u2014'} {r.client || t('jobCosting.noClient')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <JobCostingStatsBar stats={stats} />

      <JobCostingTable
        rows={filteredRows}
        loading={loading}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        search={search}
        onSearch={setSearch}
        sort={sort}
        onSort={setSort}
        onOpen={handleOpen}
      />

      <JobCostingDetailSheet
        open={detailOpen}
        onOpenChange={(o) => {
          if (!o) closeDetail();
          else setDetailOpen(true);
        }}
        row={detailRow}
        onUpdated={handleJobUpdated}
        onLogMaterial={(job) => {
          setLogJob(job);
          setLogOpen(true);
        }}
      />

      <LogMaterialDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        job={logJob}
        jobs={jobs}
        onLogged={handleJobUpdated}
      />
    </div>
  );
}
